/**
 * =============================================================================
 * Página: /afiliado/indicacoes
 * Versão: 2.0.0 (Redesign Premium Mobile-First)
 *
 * Descrição:
 *   Funil de indicações (leads) do afiliado em todos os programas.
 *   Layout premium: pílulas de filtro, cards no mobile, tabela enxuta no
 *   desktop. Status com badges suaves do tema "aff-*".
 * =============================================================================
 */
import { useMemo, useState } from 'react';
import { Users, MailOpen, Phone, Tag, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useAffiliateReferrals,
  type AffiliateReferralRow,
  type ReferralStatus,
} from '@/hooks/useAffiliateReferrals';
import { formatCurrency } from '@/lib/currency-utils';
import { cn } from '@/lib/utils';
import { AffiliatePageHeader } from '@/components/affiliate/shared/AffiliatePageHeader';
import { AffiliateEmptyState } from '@/components/affiliate/shared/AffiliateEmptyState';
// AffiliateStatusBadge removido nesta tela: status agora é dot+texto preto inline.
import {
  PeriodFilterBar,
  DEFAULT_PERIOD,
  resolvePeriod,
  type PeriodFilterValue,
} from '@/components/affiliate/PeriodFilterBar';

// Mapa de status → label + cor da bolinha (dot). Texto sempre em preto.
const STATUS_META: Record<ReferralStatus, { label: string; dotClass: string }> = {
  // Pendente em âmbar (warning) — mesmo padrão de Comissões para diferenciar de Qualificado/Convertido
  pending:   { label: 'Pendente',    dotClass: 'bg-[hsl(var(--aff-warning))]' },
  qualified: { label: 'Qualificado', dotClass: 'bg-[hsl(var(--aff-accent-2))]' },
  converted: { label: 'Convertido',  dotClass: 'bg-[hsl(var(--aff-success))]' },
  lost:      { label: 'Perdido',     dotClass: 'bg-[hsl(var(--aff-danger))]' },
  cancelled: { label: 'Cancelada',   dotClass: 'bg-[hsl(var(--aff-danger))]' },
};

/**
 * Renderiza status no padrão "dot colorido + texto preto"
 * (mesmo padrão usado na home `Últimas indicações`).
 */
function statusBadge(status: ReferralStatus) {
  const meta = STATUS_META[status] ?? { label: status, dotClass: 'bg-[hsl(var(--aff-muted-foreground))]' };
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[hsl(var(--aff-foreground))]">
      <span aria-hidden className={cn('h-2 w-2 rounded-full shrink-0', meta.dotClass)} />
      {meta.label}
    </span>
  );
}

/** Formata data com hora e minuto: dd/MM/yyyy HH:mm */
function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AffiliateReferrals() {
  // Filtro de período (Hoje · Ontem · Este mês · Personalizado)
  const [period, setPeriod] = useState<PeriodFilterValue>(DEFAULT_PERIOD);
  const { start, end } = useMemo(() => resolvePeriod(period), [period]);

  const {
    data: referrals,
    isLoading,
    isError,
    refetch,
  } = useAffiliateReferrals({ startDate: start, endDate: end });
  const [programFilter, setProgramFilter] = useState<string>('all');

  const programs = useMemo(() => {
    const map = new Map<string, string>();
    (referrals ?? []).forEach((r) => {
      if (r.program_id) {
        map.set(r.program_id, r.program?.name ?? 'Programa');
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [referrals]);

  const showProgramFilter = programs.length > 1;
  const filtered = useMemo(() => {
    if (!referrals) return [];
    if (programFilter === 'all') return referrals;
    return referrals.filter((r) => r.program_id === programFilter);
  }, [referrals, programFilter]);

  return (
    <div className="container mx-auto max-w-6xl p-4 md:p-6 space-y-6 aff-animate-in">
      <AffiliatePageHeader
        icon={<Users className="h-5 w-5" />}
        title="Indicações"
        subtitle="Acompanhe o funil de leads originados pelos seus links."
      />

      {/* Filtro de período (server-side) */}
      <PeriodFilterBar value={period} onChange={setPeriod} />

      {showProgramFilter && (
        <ProgramFilterPills
          programs={programs}
          value={programFilter}
          onChange={setProgramFilter}
          totalCount={referrals?.length ?? 0}
          countByProgram={(id) => (referrals ?? []).filter((r) => r.program_id === id).length}
        />
      )}

      {isLoading && <ReferralsSkeleton />}

      {isError && !isLoading && (
        <div className="aff-card flex flex-col items-center gap-3 px-6 py-10 text-center">
          <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
            Não foi possível carregar suas indicações.
          </p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Tentar novamente
          </Button>
        </div>
      )}

      {!isLoading && !isError && filtered.length === 0 && (
        <AffiliateEmptyState
          icon={<Users className="h-7 w-7" />}
          title={
            programFilter === 'all'
              ? 'Nenhuma indicação ainda'
              : 'Nenhuma indicação neste programa'
          }
          description={
            programFilter === 'all'
              ? 'Compartilhe seus links de divulgação para começar a receber leads aqui no seu funil.'
              : 'Selecione outro programa ou divulgue mais para captar leads.'
          }
        />
      )}

      {!isLoading && !isError && filtered.length > 0 && (
        <>
          {/* Desktop: tabela */}
          <div className="aff-card hidden md:block overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-[hsl(var(--aff-border))] hover:bg-transparent">
                  <TableHead>Lead</TableHead>
                  {showProgramFilter && <TableHead>Programa</TableHead>}
                  <TableHead>Campanha</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Criado em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow
                    key={r.id}
                    className="border-[hsl(var(--aff-border))] hover:bg-[hsl(var(--aff-surface-2))]"
                  >
                    <TableCell>
                      <div className="font-semibold text-[hsl(var(--aff-foreground))]">
                        {r.lead_name || 'Lead sem nome'}
                      </div>
                      <div className="text-xs text-[hsl(var(--aff-muted-foreground))]">
                        {r.lead_email || r.lead_phone || '—'}
                      </div>
                    </TableCell>
                    {showProgramFilter && (
                      <TableCell>
                        <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--aff-surface-2))] px-2 py-0.5 text-[11px] text-[hsl(var(--aff-muted-foreground))]">
                          <Building2 className="h-3 w-3" />
                          {r.program?.name ?? '—'}
                        </span>
                      </TableCell>
                    )}
                    <TableCell className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                      {r.campaign?.name || '—'}
                    </TableCell>
                    <TableCell>{statusBadge(r.status)}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums text-[hsl(var(--aff-foreground))]">
                      {r.commission_value && Number(r.commission_value) > 0
                        ? formatCurrency(Number(r.commission_value))
                        : '—'}
                    </TableCell>
                    <TableCell className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                      {formatDate(r.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile: cards */}
          <div className="grid gap-3 md:hidden">
            {filtered.map((r) => (
              <MobileReferralRow
                key={r.id}
                referral={r}
                showProgram={showProgramFilter}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Filtro de programa em pílulas premium. */
interface ProgramFilterPillsProps {
  programs: Array<{ id: string; name: string }>;
  value: string;
  onChange: (v: string) => void;
  totalCount: number;
  countByProgram: (id: string) => number;
}
function ProgramFilterPills({
  programs,
  value,
  onChange,
  totalCount,
  countByProgram,
}: ProgramFilterPillsProps) {
  const items = [
    { id: 'all', name: 'Todos', count: totalCount },
    ...programs.map((p) => ({ id: p.id, name: p.name, count: countByProgram(p.id) })),
  ];
  return (
    <div className="-mx-1 flex gap-2 overflow-x-auto pb-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {items.map((it) => {
        const active = value === it.id;
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => onChange(it.id)}
            className={cn(
              'shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all',
              active
                ? 'text-[hsl(var(--aff-primary-foreground))] shadow-[var(--aff-shadow-soft)]'
                : 'bg-[hsl(var(--aff-surface))] text-[hsl(var(--aff-muted-foreground))] border border-[hsl(var(--aff-border))]',
            )}
            style={
              active
                ? { background: 'var(--aff-gradient-hero)' }
                : undefined
            }
          >
            {it.name}
            <span className="ml-1.5 tabular-nums opacity-80">({it.count})</span>
          </button>
        );
      })}
    </div>
  );
}

/** Card de indicação no mobile. */
function MobileReferralRow({
  referral,
  showProgram,
}: {
  referral: AffiliateReferralRow;
  showProgram: boolean;
}) {
  return (
    <div className="aff-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate text-[hsl(var(--aff-foreground))]">
            {referral.lead_name || 'Lead sem nome'}
          </div>
          {showProgram && referral.program?.name && (
            <div className="mt-0.5 flex items-center gap-1 text-[11px] text-[hsl(var(--aff-muted-foreground))]">
              <Building2 className="h-3 w-3" />
              <span className="truncate">{referral.program.name}</span>
            </div>
          )}
          {referral.campaign?.name && (
            <div className="mt-0.5 flex items-center gap-1 text-[11px] text-[hsl(var(--aff-muted-foreground))]">
              <Tag className="h-3 w-3" />
              <span className="truncate">{referral.campaign.name}</span>
            </div>
          )}
        </div>
        {statusBadge(referral.status)}
      </div>

      {(referral.lead_email || referral.lead_phone) && (
        <div className="space-y-1 text-[11px] text-[hsl(var(--aff-muted-foreground))]">
          {referral.lead_email && (
            <div className="flex items-center gap-1.5">
              <MailOpen className="h-3 w-3" />
              <span className="truncate">{referral.lead_email}</span>
            </div>
          )}
          {referral.lead_phone && (
            <div className="flex items-center gap-1.5">
              <Phone className="h-3 w-3" />
              <span>{referral.lead_phone}</span>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between border-t border-[hsl(var(--aff-border))] pt-3 text-xs">
        <span className="text-[hsl(var(--aff-muted-foreground))]">
          {formatDate(referral.created_at)}
        </span>
        {referral.commission_value && Number(referral.commission_value) > 0 ? (
          <span className="font-bold tabular-nums text-[hsl(var(--aff-foreground))]">
            {formatCurrency(Number(referral.commission_value))}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function ReferralsSkeleton() {
  return (
    <div className="aff-card p-4 space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-6 w-16" />
        </div>
      ))}
    </div>
  );
}
